import axios from "axios";
import { supabase } from "lib/supabase";
import { isOrgAdmin } from "utils/roleChecks";

const API_BASE = import.meta.env.VITE_AI_BACKEND_URL;
// const API_BASE = "http://localhost:5000/api" || "";

/**
 * Fetch temp members (draft profiles) for an org
 * @param {string} orgId
 */
export const fetchTempMembers = async (orgId) => {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error("User not authenticated");

  const admin = await isOrgAdmin(user.id, orgId);
  if (!admin) {
    throw new Error("Only admins can view pending member drafts.");
  }

  const { data } = await axios.get(`${API_BASE}/parse-temp-members/${orgId}`);
  return data;
};

/**
 * Upload a ZIP of resumes for bulk processing
 * @param {File} zipFile
 * @param {string} orgId
 */
export const uploadBulkResumes = async (zipFile, orgId) => {
  const formData = new FormData();
  formData.append("file", zipFile);
  formData.append("orgId", orgId);

  const { data } = await axios.post(`${API_BASE}/parse-upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/**
 * Approve a temp member — creates auth user, moves resume to final storage,
 * inserts into user_profiles, cleans up draft data.
 *
 * @param {string} tempProfileId  - ID from temp_user_profiles
 * @param {object} overrides      - Admin-filled fields that override AI extraction
 * @param {string} overrides.name
 * @param {string} overrides.email
 * @param {string} overrides.phone
 * @param {string} overrides.department
 * @param {string} overrides.position
 * @param {string} overrides.experience_level
 * @param {boolean} overrides.is_sme
 * @param {string} overrides.collaborator_start_date  ISO date string or null
 * @param {string} overrides.collaborator_end_date    ISO date string or null
 * @param {string[]} overrides.skills
 */
export const approveTempMember = async (tempProfileId, overrides = {}) => {
  const { data } = await axios.post(`${API_BASE}/api/approve`, {
    tempProfileId,
    overrides,
  });
  return data; // { success, userId, temporaryPassword }
};

/**
 * Reject / delete a draft profile without promoting it
 * @param {string} tempProfileId
 */
export const rejectTempMember = async (tempProfileId) => {
  const { data } = await axios.delete(
    `${API_BASE}/api/temp-members/${tempProfileId}`,
  );
  return data;
};
